import { SupplierStatus } from '@cpce/database/enums';
import { useMemo, useState } from 'react';
import { useSupplier, useSuppliersInfinite } from '@/api/suppliers/suppliers.queries';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { Combobox, type ComboboxOptions } from './Combobox';

const NONE = -1;

interface SupplierComboboxProps {
  value: number | null | undefined;
  onChange: (supplierId: number | null) => void;
  /** Adds an option standing for « no supplier », mapped to null. */
  noneOptionLabel?: string;
  disabled?: boolean;
  error?: string;
}

/**
 * Searches suppliers on the server and loads more as the list scrolls: a CSE can have hundreds, far
 * beyond one page. Archived suppliers are not offered, except the one already selected.
 */
export function SupplierCombobox(props: SupplierComboboxProps) {
  const { value, onChange, noneOptionLabel, disabled, error } = props;
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 300);
  const {
    data: suppliers,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
  } = useSuppliersInfinite({ search: debouncedSearch || undefined, status: SupplierStatus.active });
  const { data: selected } = useSupplier(value ?? 0, { enabled: !!value });

  const options = useMemo((): ComboboxOptions<number> => {
    const list = suppliers.map((supplier) => ({ value: supplier.id, label: supplier.name }));
    if (selected && !list.some((option) => option.value === selected.id)) {
      list.unshift({ value: selected.id, label: selected.name });
    }
    return noneOptionLabel ? [{ value: NONE, label: noneOptionLabel }, ...list] : list;
  }, [suppliers, selected, noneOptionLabel]);

  const comboboxValue = value === null && noneOptionLabel ? NONE : value || undefined;

  return (
    <Combobox
      options={options}
      value={comboboxValue}
      onChange={(next) => onChange(next === NONE ? null : next)}
      onSearchChange={setSearch}
      onScrollEnd={() => {
        if (hasNextPage && !isFetchingNextPage) fetchNextPage();
      }}
      disabled={disabled}
      error={error}
      // The typed search only reaches the server once the debounce settles: until then the list
      // still holds the previous results, which would read as « aucun résultat ».
      isLoading={isLoading || isFetchingNextPage || search !== debouncedSearch}
    />
  );
}
